/**
 * CenoX AI Chat.
 *
 * Threads live on the server, so closing the tab no longer loses the
 * conversation — and the history the model is sent is the history that was
 * actually said, rather than whatever the browser chose to re-send.
 *
 * A conversation is private to the person who started it. That is enforced by
 * a row-level policy, not by this screen: what the server returns is already
 * only what the caller may see, so there is no filtering here to forget.
 *
 * Answers stream. The assistant's words appear as they are generated into a
 * bubble that is not yet a saved message; when the stream finishes the thread
 * is refetched and the real message takes its place. Exactly one terminal
 * event always arrives — `done` or `error` — so the screen never has to guess
 * from a closed socket whether the answer was charged.
 *
 * The open thread is in the address — `/chat/<id>` — so a conversation can be
 * bookmarked, sent to a colleague it was shared with, or reopened after a
 * reload. One route with an optional segment rather than two, so starting a
 * thread mid-question changes the address without remounting this screen and
 * cutting off the answer it is streaming. Chat or Work is in the address too
 * (`?mode=work`), which is what the sidebar's Work entry opens.
 *
 * A new conversation leads with the welcome and a large composer; history is
 * a panel you open, not a column you always pay for. A workspace without
 * CenoX AI sees the same welcome with the composer shut and the way to get it
 * — and this screen asks the AI endpoints nothing on its behalf.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Loader2, MessageSquarePlus, PanelLeft, Share2 } from 'lucide-react';
import {
  type AiCitation,
  type AiConversationView,
  type AiProjectView,
  type AiMessageView,
  type AiRetrievalSummary,
  type AiStreamDone,
  type AiTiersResponse,
} from '@cenox/shared';
import { ApiError, api, streamEvents } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { usePlatformProducts } from '../../lib/platformProducts';
import { Spinner, cn } from '../../components/ui';
import { CenoxMark } from '../../components/platform/CenoxMark';
import { Composer, type ComposerHandle, type Mode } from './chat/Composer';
import { HistoryPanel } from './chat/HistoryPanel';
import { ChatWelcome } from './chat/ChatWelcome';
import { AnswerText as WebAnswerText, WebSources } from './chat/WebSources';
import { ProjectRail, type ProjectScope } from './ProjectRail';
import { FileTextDrawer, type FileTextTarget } from './files/FileTextDrawer';

interface Thread {
  conversation: AiConversationView;
  messages: AiMessageView[];
}

/** What Home hands over when you ask from there: the question, sent on arrival. */
export interface ChatHandover {
  prompt?: string;
}

/** One question as sent, kept whole so a retry is the same request. */
interface PendingAsk {
  conversationId: string;
  body: { message: string; tier: string; escalationReason?: string; webSearch?: boolean; idempotencyKey: string };
}

type Outcome = { kind: 'done' } | { kind: 'failed'; message: string } | { kind: 'interrupted' };

/**
 * How long to wait before each automatic retry of a dropped answer — about
 * half a minute in all, longer than an answer takes to finish on the server.
 */
const RECOVERY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

const LOST_CONNECTION =
  'The connection dropped before the answer arrived. Try again to fetch it — you will not be charged twice.';

/** A key for one question, reused by every retry of it. */
function newRequestKey(): string {
  return `chat-${
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }`;
}

/** Wait, unless the person leaves first. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * What the `done` event said about one answer that the saved message does not
 * carry: the citations the model invented, and what the documents
 * contributed. Kept for this visit only; after a reload the saved message's
 * own `citations` are what links.
 */
interface AnswerNotes {
  citations: AiCitation[];
  unverified: number[];
  documents: AiRetrievalSummary | null;
}

const WIDE = '(min-width: 1024px)';
const HISTORY_KEY = 'cenox.chat.history';

/** Laptop and up: history docks beside the conversation instead of covering it. */
function useWide(): boolean {
  const [wide, setWide] = useState(() => typeof window.matchMedia === 'function' && window.matchMedia(WIDE).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(WIDE);
    const onChange = (): void => setWide(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return wide;
}

function rememberedHistory(): boolean {
  try {
    return window.localStorage.getItem(HISTORY_KEY) === 'open';
  } catch {
    return false;
  }
}

export function ChatView({ access = 'open' }: { access?: 'open' | 'locked' }): ReactNode {
  const { business, user } = useAuth();
  const path = useBusinessPath();
  const client = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const products = usePlatformProducts();
  const wide = useWide();
  const composer = useRef<ComposerHandle>(null);

  const aiReady = access === 'open';
  const aiKnown = aiReady || (!products.isPending && !products.isError);
  const ai = products.data?.data.find((p) => p.product === 'ai');
  const automation = products.data?.data.find((p) => p.product === 'automation');

  const openId = useParams<{ conversationId: string }>().conversationId ?? null;
  /** The address is the open thread. Opening one from `/chat` refines it rather than adding a page. */
  const setOpenId = (id: string | null): void => {
    if (id === openId) return;
    navigate(
      { pathname: id ? `/chat/${id}` : '/chat', search: location.search },
      { replace: !openId && id !== null },
    );
  };

  const [params, setParams] = useSearchParams();
  const mode: Mode = params.get('mode') === 'work' ? 'work' : 'chat';
  const setMode = (next: Mode): void => {
    const p = new URLSearchParams(params);
    if (next === 'work') p.set('mode', 'work');
    else p.delete('mode');
    setParams(p, { replace: true, state: location.state });
  };

  // `?history=open` is how the Projects page sends you to the folders.
  const [historyOpen, setHistoryOpen] = useState(
    () => params.get('history') === 'open' || (wide && rememberedHistory()),
  );
  const toggleHistory = (next: boolean): void => {
    setHistoryOpen(next);
    if (!wide) return;
    try {
      if (next) window.localStorage.setItem(HISTORY_KEY, 'open');
      else window.localStorage.removeItem(HISTORY_KEY);
    } catch {
      // Not remembered; still applied for this visit.
    }
  };

  const [search, setSearch] = useState('');
  /** Which folder the list is showing — and where a new thread is started. See `ProjectScope`. */
  const [scope, setScope] = useState<ProjectScope>({ kind: 'all' });
  const [draft, setDraft] = useState('');
  const [tier, setTier] = useState<string>('');
  /** Required by the server for any tier above the default. */
  const [reason, setReason] = useState('');
  /**
   * Search the web for the next answers. Stays on until turned off, and is
   * only ever sent when the server offers it — the server decides anyway.
   */
  const [webSearch, setWebSearch] = useState(false);
  /** A thread this visit just started: it already carries the model and reason you chose. */
  const justStarted = useRef<string | null>(null);
  /** The answer as it arrives — not a saved message until the stream ends. */
  const [streaming, setStreaming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The question whose answer did not arrive, kept so Try again resends it under the same key. */
  const [unanswered, setUnanswered] = useState<PendingAsk | null>(null);
  const abort = useRef<AbortController | null>(null);
  /** Per saved answer, by message id. See `AnswerNotes`. */
  const [notes, setNotes] = useState<Record<string, AnswerNotes>>({});
  /** A document opened from a citation, at the passage it points to. */
  const [reading, setReading] = useState<FileTextTarget | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);

  /*
    The scope becomes a query parameter, and `unfiled` has to be sayable —
    `projectId=none` means "in no project", which is a different request from
    omitting the parameter.
  */
  const scopeParam =
    scope.kind === 'all' ? null : scope.kind === 'unfiled' ? 'none' : scope.id;

  const conversations = useQuery({
    queryKey: ['ai-conversations', business?.businessId, search, scopeParam],
    queryFn: () => {
      const query = new URLSearchParams();
      if (search.trim()) query.set('q', search.trim());
      if (scopeParam) query.set('projectId', scopeParam);
      const qs = query.toString();
      return api.get<{ data: AiConversationView[] }>(path(`/ai/conversations${qs ? `?${qs}` : ''}`));
    },
    enabled: Boolean(business) && aiReady,
  });

  /** For Choose project and the move control. Archived ones are not destinations. */
  const projectOptions = useQuery({
    queryKey: ['ai-projects', business?.businessId, false],
    queryFn: () => api.get<{ data: AiProjectView[] }>(path('/ai/projects')),
    enabled: Boolean(business) && aiReady,
  });
  const projects = projectOptions.data?.data ?? [];

  const thread = useQuery({
    queryKey: ['ai-conversation', business?.businessId, openId],
    queryFn: () => api.get<Thread>(path(`/ai/conversations/${openId!}`)),
    enabled: Boolean(business) && Boolean(openId) && aiReady,
  });

  /** Which tiers this plan permits. A choice is only offered where there is one. */
  const tiers = useQuery({
    queryKey: ['ai-tiers', business?.businessId],
    queryFn: () => api.get<AiTiersResponse>(path('/ai/tiers')),
    enabled: Boolean(business) && aiReady,
    staleTime: 5 * 60_000,
  });
  const allowed = (tiers.data?.data ?? []).filter((t) => t.available);
  /*
    Never in a project: the server refuses it there (a project's documents and
    instructions must not reach a public search), so it is not offered either.
    A thread still loading counts as a project thread until it says otherwise.
  */
  const loaded = thread.data?.conversation;
  const inProject = openId ? (loaded ? Boolean(loaded.projectId) : true) : scope.kind === 'project';
  const searchOffer = tiers.data?.webSearch?.available && !inProject ? tiers.data.webSearch : null;
  const searching = webSearch && searchOffer !== null;

  const refreshList = () => client.invalidateQueries({ queryKey: ['ai-conversations'] });

  const startThread = useMutation({
    mutationFn: () =>
      api.post<AiConversationView>(path('/ai/conversations'), {
        ...(tier ? { tier } : {}),
        // Start it where you are looking. A thread begun inside a project is
        // still private — sharing a project does not publish what is typed in it.
        ...(scope.kind === 'project' ? { projectId: scope.id } : {}),
      }),
    onSuccess: (created) => {
      justStarted.current = created.id;
      setOpenId(created.id);
      setStreaming(null);
      setError(null);
      if (!wide) setHistoryOpen(false);
      void refreshList();
    },
  });

  const removeThread = useMutation({
    mutationFn: (id: string) => api.del(path(`/ai/conversations/${id}`)),
    onSuccess: (_r, id) => {
      if (openId === id) setOpenId(null);
      void refreshList();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not delete that conversation.'),
  });

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.patch(path(`/ai/conversations/${id}`), { title }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ai-conversation'] });
      void refreshList();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not rename that conversation.'),
  });

  const move = useMutation({
    mutationFn: (input: { id: string; projectId: string | null }) =>
      api.patch(path(`/ai/conversations/${input.id}`), { projectId: input.projectId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ai-conversation'] });
      void client.invalidateQueries({ queryKey: ['ai-projects'] });
      void refreshList();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not move that conversation.'),
  });

  /*
    A widening the server has refused until it is confirmed, held with the
    sentence the server gave — which counts the messages and names who would
    gain sight. Re-deriving that in the browser would be a second copy of a
    rule that can only be right in one place.
  */
  const [pendingShare, setPendingShare] = useState<
    { id: string; visibility: string; warning: string } | null
  >(null);

  const share = useMutation({
    mutationFn: (input: { id: string; visibility: string; acknowledgeHistory?: boolean }) =>
      api.patch(path(`/ai/conversations/${input.id}`), {
        visibility: input.visibility,
        ...(input.acknowledgeHistory ? { acknowledgeHistory: true } : {}),
      }),
    onSuccess: () => {
      setPendingShare(null);
      void client.invalidateQueries({ queryKey: ['ai-conversation'] });
      void refreshList();
    },
    onError: (caught, input) => {
      /*
        The server refuses a widening that has history in it, and the refusal
        is a question rather than a failure — so it becomes a confirmation
        step instead of an error message.
      */
      const asked = caught instanceof ApiError ? caught.fields?.acknowledgeHistory?.[0] : undefined;
      if (asked) {
        setPendingShare({ id: input.id, visibility: input.visibility, warning: asked });
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not change who can see this.');
    },
  });

  // Follow the answer as it grows, the way a chat is expected to behave.
  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' });
  }, [thread.data?.messages?.length, streaming]);

  // Leaving mid-answer stops the reading; the server still finishes and saves
  // it, because the provider has already been paid for it.
  useEffect(() => () => abort.current?.abort(), []);

  async function ask(event: FormEvent): Promise<void> {
    event.preventDefault();
    await send(draft.trim());
  }

  async function send(message: string): Promise<void> {
    // Belt as well as braces: Send is disabled in Work and when locked, and sending is refused.
    if (!message || streaming !== null || mode === 'work' || !aiReady) return;
    // A stronger model needs its reason; the server would refuse it without one.
    if (tier && !reason.trim()) return;

    let id = openId;
    if (!id) {
      const created = await startThread.mutateAsync();
      id = created.id;
    }

    setDraft('');
    await deliver({
      conversationId: id,
      /*
        The tier is always named, `default` included. Unnamed, the server
        falls back to the tier the thread was started at — so a thread begun
        on a stronger model would bill that model (and demand its reason)
        while the picker showed the default. What the picker shows is what
        is sent.

        The key is made once per question and travels with every retry of
        it, which is what lets the server tell "the same question again"
        from "a new question" — and charge once.
      */
      body: {
        message,
        tier: tier || 'default',
        ...(tier ? { escalationReason: reason.trim() } : {}),
        ...(searching ? { webSearch: true } : {}),
        idempotencyKey: newRequestKey(),
      },
    });
  }

  /**
   * Ask, and if the connection drops, ask again — the SAME request.
   *
   * A dropped stream says nothing about the answer: the server usually
   * finishes it, and charges for it, because the provider has been paid. So a
   * drop is never reported as a failure straight away. The request is sent
   * again under its key: a finished answer comes back as it was, uncharged; a
   * running one is reported as still in progress, and waited for.
   */
  async function deliver(request: PendingAsk): Promise<void> {
    setError(null);
    setUnanswered(null);
    setStreaming('');
    const controller = new AbortController();
    abort.current = controller;

    try {
      for (let attempt = 0; ; attempt += 1) {
        const outcome = await attemptOnce(request, controller.signal);
        if (outcome.kind === 'done' || controller.signal.aborted) return;
        if (outcome.kind === 'failed') {
          setError(outcome.message);
          setUnanswered(request);
          return;
        }
        const delay = RECOVERY_DELAYS_MS[attempt];
        if (delay === undefined) {
          setError(LOST_CONNECTION);
          setUnanswered(request);
          return;
        }
        setStreaming('');
        await pause(delay, controller.signal);
      }
    } finally {
      abort.current = null;
      setStreaming(null);
      // The streamed text was never a saved message; the thread is the truth.
      void client.invalidateQueries({ queryKey: ['ai-conversation'] });
      // The day's remaining searches moved.
      if (request.body.webSearch) void client.invalidateQueries({ queryKey: ['ai-tiers'] });
      void refreshList();
    }
  }

  async function attemptOnce(request: PendingAsk, signal: AbortSignal): Promise<Outcome> {
    let answer = '';
    const result: { outcome: Outcome | null } = { outcome: null };
    try {
      await streamEvents(
        path(`/ai/conversations/${request.conversationId}/messages`),
        request.body,
        (name, data) => {
          if (name === 'chunk') {
            answer += (data as { text: string }).text;
            setStreaming(answer);
          }
          if (name === 'error') {
            const failure = data as { code?: string; message: string };
            result.outcome =
              failure.code === 'ai_request_in_progress' ? { kind: 'interrupted' } : { kind: 'failed', message: failure.message };
          }
          if (name === 'done') {
            const done = data as AiStreamDone;
            setNotes((all) => ({
              ...all,
              [done.messageId]: {
                citations: done.citations ?? [],
                unverified: done.unverifiedCitations ?? [],
                documents: done.documents ?? null,
              },
            }));
            result.outcome = { kind: 'done' };
          }
        },
        signal,
      );
    } catch (caught) {
      if (signal.aborted) return { kind: 'interrupted' };
      // Refused before the stream began: a refusal stands; a server or
      // gateway fault (a deploy, a proxy timeout) is worth another try.
      if (caught instanceof ApiError && caught.status < 500 && caught.code !== 'ai_request_in_progress') {
        return { kind: 'failed', message: caught.message };
      }
      return { kind: 'interrupted' };
    }
    // A stream that closed without `done` or `error` was cut, not finished.
    return result.outcome ?? { kind: 'interrupted' };
  }

  /*
    A question asked on Home arrives as navigation state and is sent once.

    The state is cleared before sending, so reloading the page — which would
    otherwise replay the history entry with its state — cannot ask, and
    charge, a second time.
  */
  const handedOver = useRef(false);
  useEffect(() => {
    const prompt = (location.state as ChatHandover | null)?.prompt?.trim();
    if (!prompt || handedOver.current) return;
    handedOver.current = true;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    void send(prompt);
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = conversations.data?.data ?? [];
  const open = thread.data?.conversation;

  /*
    Opening a thread shows the model it is on. A thread started on a stronger
    tier stays there unless you choose otherwise — and choosing it again asks
    again why, because each request spends it. A tier this plan no longer
    offers falls back to the default, visibly.
  */
  useEffect(() => {
    if (!open || open.id === justStarted.current) return;
    const kept = open.tier && open.tier !== 'default' && allowed.some((t) => t.tier === open.tier);
    setTier(kept ? open.tier! : '');
    setReason('');
    // Per thread, and once the plan's tiers are known — not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.id, open?.tier, allowed.length]);
  const firstName = user?.fullName?.trim().split(/\s+/)[0];
  const inConversation = Boolean(openId) || streaming !== null;
  /*
    The instructions in force on the open thread, if any. Read from the list
    already fetched for the move control rather than with a request of its own.
  */
  const activeInstructions = open?.projectId
    ? (projects.find((p) => p.id === open.projectId)?.instructions ?? null)
    : null;

  /*
    Choose project, on a new conversation: the folder the first message will
    start the thread in. It is the same scope the history list shows, so
    picking a project in either place means the same thing. An open thread is
    moved from its header instead.
  */
  const projectPicker = aiReady ? (
    <label className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 focus-within:border-brand-400">
      <FolderOpen className="size-3.5 shrink-0 text-slate-400" aria-hidden />
      <span className="sr-only">Choose project</span>
      <select
        aria-label="Choose project"
        value={scope.kind === 'project' ? scope.id : ''}
        onChange={(e) => setScope(e.target.value ? { kind: 'project', id: e.target.value } : { kind: 'all' })}
        className="max-w-[10rem] truncate bg-transparent pr-1 outline-none"
      >
        <option value="">Choose project</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  ) : null;

  const composerProps = {
    mode,
    value: draft,
    onChange: setDraft,
    onSubmit: (e: FormEvent) => void ask(e),
    locked: !aiReady,
    busy: streaming !== null,
    models: allowed,
    model: tier,
    onModel: (next: string) => {
      setTier(next);
      if (!next) setReason('');
    },
    reason,
    onReason: setReason,
    projectControl: inConversation ? null : projectPicker,
    ...(searchOffer
      ? {
          webSearch: {
            on: webSearch,
            onToggle: setWebSearch,
            charged: searchOffer.charged,
            remainingToday: Math.max(0, searchOffer.dailyLimit - searchOffer.usedToday),
          },
        }
      : {}),
    lockedPlaceholder: !aiKnown
      ? 'Checking what this workspace can use…'
      : business
        ? 'CenoX AI is not part of this workspace'
        : 'You don’t have a workspace yet',
  };

  const history =
    historyOpen && aiReady ? (
      <HistoryPanel
        variant={wide ? 'docked' : 'sheet'}
        rail={<ProjectRail scope={scope} onScope={setScope} onError={setError} />}
        rows={rows}
        pending={conversations.isPending}
        search={search}
        onSearch={setSearch}
        openId={openId}
        onOpen={(id) => {
          setOpenId(id);
          setStreaming(null);
          setError(null);
          setUnanswered(null);
          if (!wide) setHistoryOpen(false);
        }}
        onNew={() => {
          // A thread is made by its first message, not by opening a blank page.
          setOpenId(null);
          setStreaming(null);
          setError(null);
          setUnanswered(null);
          if (!wide) setHistoryOpen(false);
          composer.current?.focus();
        }}
        creating={false}
        onRename={(id, title) => rename.mutate({ id, title })}
        onDelete={(id) => removeThread.mutate(id)}
        onClose={() => toggleHistory(false)}
      />
    ) : null;

  return (
    <div className="-mx-4 -my-6 flex h-[calc(100dvh-3.5rem)] gap-4 px-3 py-3 sm:-mx-6 sm:px-4 lg:-mx-8 lg:px-5">
      {history}

      <section className="relative flex min-w-0 flex-1 flex-col">
        {/* ── History, Chat | Work, New chat ─────────────────────────────── */}
        <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 pb-2">
          <div>
            {aiReady && !historyOpen && (
              <button
                type="button"
                onClick={() => toggleHistory(true)}
                aria-label="Open history"
                title="History"
                className="inline-flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-brand-500"
              >
                <PanelLeft className="size-4" aria-hidden />
                <span className="hidden sm:inline">History</span>
              </button>
            )}
          </div>

          <div role="group" aria-label="Chat or Work" className="flex rounded-full bg-slate-100 p-1">
            {(['chat', 'work'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                className={cn(
                  'rounded-full px-6 py-1.5 text-sm font-medium transition-all sm:px-8',
                  'focus-visible:outline-2 focus-visible:outline-brand-500',
                  mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800',
                )}
              >
                {m === 'chat' ? 'Chat' : 'Work'}
              </button>
            ))}
          </div>

          <div className="flex justify-end">
            {aiReady && inConversation && (
              <button
                type="button"
                onClick={() => setOpenId(null)}
                aria-label="New chat"
                title="New chat"
                className="inline-flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-brand-500"
              >
                <MessageSquarePlus className="size-4" aria-hidden />
                <span className="hidden sm:inline">New chat</span>
              </button>
            )}
          </div>
        </div>

        {!inConversation ? (
          /* ── A new conversation ─────────────────────────────────────── */
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ChatWelcome
              firstName={firstName}
              aiReady={aiReady}
              aiKnown={aiKnown}
              hasWorkspace={Boolean(business)}
              ai={ai}
              automation={automation}
              onAnalyse={() => {
                setMode('chat');
                setDraft('Analyse this workspace’s sales over the last 30 days');
                composer.current?.focus();
              }}
              composer={<Composer ref={composer} variant="hero" {...composerProps} />}
            />
            {error && (
              <p role="alert" className="mx-auto max-w-3xl text-center text-sm text-red-700">
                {error}
              </p>
            )}
          </div>
        ) : (
          /* ── The conversation ───────────────────────────────────────── */
          <>
            {open && (
              <header className="mx-auto flex w-full max-w-3xl shrink-0 flex-wrap items-center gap-2 px-1 pb-2 sm:flex-nowrap">
                {/* Its own line on a phone, so the controls beside it cannot squeeze it to nothing. */}
                <h1 className="min-w-0 basis-full truncate text-sm font-semibold text-slate-900 sm:flex-1 sm:basis-auto">
                  {open.title ?? 'New conversation'}
                </h1>

                {/*
                  Said on the thread, not only on the project: somebody reading
                  an answer is looking here, so if instructions are shaping the
                  reply, it says so where the reply is.
                */}
                {activeInstructions && (
                  <span
                    title={activeInstructions}
                    className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700"
                  >
                    Project instructions active
                  </span>
                )}

                {/*
                  Where it is filed. A select rather than drag-and-drop: it has
                  to work on a phone. Only the author sees it — moving a thread
                  is the author's decision, and the server refuses anybody else.
                */}
                {open.mine && (
                  <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-500">
                    <span className="hidden sm:inline">Project</span>
                    <select
                      aria-label="Project for this conversation"
                      value={open.projectId ?? ''}
                      disabled={move.isPending}
                      onChange={(e) => move.mutate({ id: open.id, projectId: e.target.value || null })}
                      className="h-8 max-w-[9rem] truncate rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus-visible:border-brand-400 sm:max-w-[11rem]"
                    >
                      <option value="">No project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {open.mine && (
                  /*
                    Three states (#62), so a toggle will not do. Sharing with the
                    project is offered only when the thread is in one. The
                    wording says who gains sight, because that is the decision.
                  */
                  <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-500">
                    <Share2 className="size-4 text-slate-400" aria-hidden />
                    <select
                      aria-label="Who can see this conversation"
                      value={open.visibility}
                      disabled={share.isPending}
                      onChange={(e) => share.mutate({ id: open.id, visibility: e.target.value })}
                      className="h-8 max-w-[9rem] truncate rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus-visible:border-brand-400 sm:max-w-[13rem]"
                    >
                      <option value="private">Only me</option>
                      {open.projectId ? <option value="project">The project members</option> : null}
                      <option value="workspace">Everyone in the workspace</option>
                    </select>
                  </label>
                )}
              </header>
            )}

            {/*
              The confirmation the server asked for (#62): its own sentence,
              which counts the messages and names who would gain sight. Sharing
              is not the default and is not pre-focused.
            */}
            {pendingShare && (
              <div
                role="alertdialog"
                aria-label="Confirm sharing this conversation"
                className="mx-auto mb-2 w-full max-w-3xl rounded-2xl border border-amber-300 bg-amber-50 p-3"
              >
                <p className="text-sm text-amber-900">{pendingShare.warning}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingShare(null)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Keep it private
                  </button>
                  <button
                    type="button"
                    disabled={share.isPending}
                    onClick={() =>
                      share.mutate({ id: pendingShare.id, visibility: pendingShare.visibility, acknowledgeHistory: true })
                    }
                    className="bg-cx-gradient inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white hover:brightness-110 disabled:opacity-60"
                  >
                    {share.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                    Share it anyway
                  </button>
                </div>
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-3xl space-y-5 px-1 py-4">
                {thread.isPending && openId && <Spinner label="Loading conversation" />}
                {(thread.data?.messages ?? []).map((m) =>
                  m.role === 'assistant' ? (
                    <Bubble key={m.id} role="assistant">
                      {m.withheld ? (
                        /*
                          An answer drawn from project documents this reader
                          may not read now. The server sends a notice in place
                          of the text; it is shown as a notice, not as an
                          answer, so nobody mistakes it for what was said.
                        */
                        <p className="text-sm italic text-slate-500" role="note">
                          {m.content}
                        </p>
                      ) : m.contextKind === 'web' ? (
                        /*
                          Links are followable only in a searched answer, whose
                          sources are public pages. Anywhere else a link is text
                          the model wrote after reading private material, and a
                          planted instruction can make it write one that carries
                          that material in its address.
                        */
                        <>
                          <WebAnswerText text={m.content} />
                          <WebSources calls={m.webSearchCalls} sources={m.sources} />
                        </>
                      ) : (
                        <>
                          <AnswerText
                            content={m.content}
                            citations={m.citations?.length ? m.citations : (notes[m.id]?.citations ?? [])}
                            unverified={notes[m.id]?.unverified ?? []}
                            onOpen={setReading}
                          />
                          {notes[m.id]?.documents && <DocumentsLine summary={notes[m.id]!.documents!} />}
                        </>
                      )}
                    </Bubble>
                  ) : (
                    <Bubble key={m.id} role="user">
                      {m.content}
                    </Bubble>
                  ),
                )}
                {streaming !== null && (
                  <Bubble role="assistant">
                    {streaming === '' ? (
                      <span className="inline-flex items-center gap-2 text-slate-500">
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                        {/* "May search", not "searching": whether it does is the model's call. */}
                        {searching ? 'Thinking — may search the web…' : 'Thinking…'}
                      </span>
                    ) : (
                      // Plain while it arrives; a searched answer's links work once it is saved.
                      streaming
                    )}
                  </Bubble>
                )}
                <div ref={bottom} />
              </div>
            </div>

            <div className="mx-auto w-full max-w-3xl shrink-0 pt-2">
              {error && (
                <p role="alert" className="mb-2 flex flex-wrap items-center gap-x-3 px-2 text-sm text-red-700">
                  <span>{error}</span>
                  {unanswered && streaming === null && (
                    <button
                      type="button"
                      className="font-medium underline underline-offset-2"
                      onClick={() => void deliver(unanswered)}
                    >
                      Try again
                    </button>
                  )}
                </p>
              )}
              <Composer ref={composer} variant="dock" {...composerProps} />
            </div>
          </>
        )}
      </section>

      {reading && <FileTextDrawer target={reading} onClose={() => setReading(null)} />}
    </div>
  );
}

const CITATION = /\[(\d+)\]/g;

/**
 * An answer, with each `[n]` that names a passage it was given turned into a
 * way to read that passage.
 *
 * Built from text pieces and elements, never from HTML: the answer is the
 * model's words, and the model has read documents anybody in the project
 * could have written. Three kinds of marker, said differently:
 *
 *   - a citation you can follow: a button that opens the passage;
 *   - one that pointed somewhere you can no longer see: plain, and says so;
 *   - one the model made up (`unverifiedCitations`): struck through, never a
 *     link, because a link would lend it the authority it does not have.
 *
 * A number that matches nothing at all stays as the text it was.
 */
function AnswerText({
  content,
  citations,
  unverified,
  onOpen,
}: {
  content: string;
  citations: AiCitation[];
  unverified: number[];
  onOpen: (target: FileTextTarget) => void;
}): ReactNode {
  if (citations.length === 0 && unverified.length === 0) return content;

  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of content.matchAll(CITATION)) {
    const at = match.index;
    const n = Number(match[1]);
    const marker = match[0];
    if (at > last) parts.push(content.slice(last, at));
    last = at + marker.length;

    if (unverified.includes(n)) {
      parts.push(
        <span key={at} title="Not a real source: the answer was given no passage with this number" className="text-red-700">
          <s>{marker}</s>
          <span className="ml-0.5 text-[10px] font-medium uppercase tracking-wide">not a real source</span>
        </span>,
      );
      continue;
    }

    const cited = citations.find((c) => c.n === n);
    if (!cited) {
      parts.push(marker);
    } else if (!cited.available || !cited.projectId || !cited.fileId || cited.ordinal == null) {
      parts.push(
        <span
          key={at}
          role="note"
          title="Source no longer available to you"
          aria-label={`${marker} Source no longer available to you`}
          className="text-slate-400"
        >
          {marker}
        </span>,
      );
    } else {
      const { projectId, fileId, ordinal, title, locator } = cited;
      parts.push(
        <button
          key={at}
          type="button"
          onClick={() =>
            onOpen({ projectId, fileId, title, offset: Math.max(0, ordinal - 1), focusOrdinal: ordinal })
          }
          aria-label={`Source ${n}: ${title ?? 'document'}${locator ? `, ${locator}` : ''}`}
          title={`${title ?? 'Document'}${locator ? ` · ${locator}` : ''}`}
          className="mx-px rounded px-0.5 font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-brand-500"
        >
          {marker}
        </button>,
      );
    }
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts;
}

/** What the project's documents contributed to an answer, in one line. */
function DocumentsLine({ summary }: { summary: AiRetrievalSummary }): ReactNode {
  const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;
  return (
    <p className="mt-2 text-xs text-slate-500">
      Searched {plural(summary.searched, 'document', 'documents')}
      {summary.notSearched > 0 ? ` · ${summary.notSearched} couldn’t be read` : ''}
      {` · ${plural(summary.passages, 'passage', 'passages')}`}
    </p>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: ReactNode }): ReactNode {
  if (role === 'user') {
    return (
      <div className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-cx-gradient px-4 py-2.5 text-sm text-white shadow-sm">
        {children}
      </div>
    );
  }
  return (
    <div className="flex max-w-full gap-3">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white">
        <CenoxMark className="size-4" />
      </span>
      <div className="min-w-0 flex-1 whitespace-pre-wrap pt-0.5 text-sm leading-relaxed text-slate-800">{children}</div>
    </div>
  );
}
